import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  IconButton,
  Chip,
  Avatar,
  useTheme,
  alpha,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Description as DocumentIcon,
  Public as PublicIcon,
  Lock as PrivateIcon,
  Event as EventIcon,
  Person as PersonIcon,
  Folder as FolderIcon,
  CloudDownload as CloudDownloadIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import axiosInstance from '../../../utils/axios';
import { useAuth } from '../../../contexts/AuthContext';
import ListFilterBar from '../../common/ListFilterBar';
import Header from '../../common/Header';
import Loading from '../../common/Loading';
import FeedbackMessage from '../../common/FeedbackMessage';
import StatCards from '../../common/StatCards';
import TableList from '../../common/TableList';
import ListBulkToolbar from '../../common/ListBulkToolbar';
import { DOCUMENT_BULK_CONFIG } from '../../../utils/bulkUpdateConfigs';
import { useListBulkSelection, bulkTableProps, createBulkHandlers } from '../../../utils/listBulkSelection';

import ConfirmDeleteDialog from '../../common/ConfirmDeleteDialog';
import { SUCCESS } from '../../common/SaaSTokens';
import { canManageDocuments, canViewPrivateDocuments } from '../../../utils/documentPermissions';
import { downloadDocumentFile } from '../../../utils/protectedFilePreview';

const DocumentList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { user } = useAuth();
  const { selectedIds, setSelectedIds, clearSelection } = useListBulkSelection();


  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(location.state?.error || null);
  const [success, setSuccess] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [activeStatFilter, setActiveStatFilter] = useState('all');

  const canManage = canManageDocuments(user);
  const canViewPrivate = canViewPrivateDocuments(user);

  const filterOptions = [
    {
      name: 'event_presence',
      label: 'Event',
      type: 'select',
      options: [
        { value: 'with', label: 'With Event' },
        { value: 'without', label: 'Without Event' },
      ],
    },
    ...(canManage
      ? [
          {
            name: 'publicity',
            label: 'Publicity',
            type: 'select',
            options: [
              { value: 'public', label: 'Public' },
              ...(canViewPrivate ? [{ value: 'private', label: 'Private' }] : []),
              { value: 'specific', label: 'Specific Members' },
            ],
          },
        ]
      : []),
  ];

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchDocuments();
  }, [user]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/documents');
      setDocuments(response.data);
      setError(null);
    } catch (error) {
      console.error('Error fetching documents:', error);
      setError('Failed to load documents. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (documentItem) => {
    if (!canManage) return;
    setSelectedDocument(documentItem);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!canManage) return;
    try {
      await axiosInstance.delete(`/documents/${selectedDocument.id}`);
      setDocuments(documents.filter((doc) => doc.id !== selectedDocument.id));
      setDeleteDialogOpen(false);
      setSelectedDocument(null);
      setSuccess(`Document "${selectedDocument.document_name}" has been deleted successfully!`);
    } catch (error) {
      console.error('Error deleting document:', error);
      setError('Failed to delete document. Please try again.');
    }
  };

  const handleDownload = async (documentItem) => {
    try {
      await downloadDocumentFile(documentItem.id, documentItem.document_name);
    } catch (error) {
      console.error('Error downloading document:', error);
      setError(error.message || 'Failed to download document. Please try again.');
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPage(0);
  };

  const handleClearFilters = () => {
    setFilters({});
    setSearchTerm('');
    setActiveStatFilter('all');
    setPage(0);
  };

  const handleStatCardClick = (_, index) => {
    const filterMap = canManage
      ? ['all', 'public', 'private', 'event']
      : ['all', 'event', 'without_event'];
    const selectedFilter = filterMap[index] || 'all';
    const nextFilter = activeStatFilter === selectedFilter ? 'all' : selectedFilter;
    setActiveStatFilter(nextFilter);
    const nextFilters = { ...filters };
    if (nextFilter === 'all') {
      delete nextFilters.publicity;
      delete nextFilters.event_presence;
    } else if (nextFilter === 'event') {
      nextFilters.event_presence = 'with';
      delete nextFilters.publicity;
    } else if (nextFilter === 'without_event') {
      nextFilters.event_presence = 'without';
      delete nextFilters.publicity;
    } else {
      nextFilters.publicity = nextFilter;
      delete nextFilters.event_presence;
    }
    setFilters(nextFilters);
    setPage(0);
  };

  const getPublicityColor = (publicity) => {
    switch (publicity) {
      case 'public':
        return 'success';
      case 'private':
        return 'error';
      case 'specific':
        return 'info';
      default:
        return 'default';
    }
  };

  const getPublicityIcon = (publicity) => {
    switch (publicity) {
      case 'public':
        return <PublicIcon fontSize="small" />;
      case 'private':
        return <PrivateIcon fontSize="small" />;
      case 'specific':
        return <PrivateIcon fontSize="small" />;
      default:
        return null;
    }
  };

  const filteredDocuments = documents.filter((doc) => {
    const matchesSearch = doc.document_name
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    const matchesPublicity =
      !filters.publicity || doc.publicity === filters.publicity;
    const hasEvent = Boolean(doc.event);
    const matchesEventPresence =
      !filters.event_presence ||
      (filters.event_presence === 'with' && hasEvent) ||
      (filters.event_presence === 'without' && !hasEvent);
    return matchesSearch && matchesPublicity && matchesEventPresence;
  });

  // ─── STATS CALCULATED FROM ALL DATA (NOT FILTERED) ───────────────────────────────
  const statsData = canManage
    ? [
        {
          icon: FolderIcon,
          value: documents.length,
          label: 'Total Documents',
          color: 'info',
        },
        {
          icon: PublicIcon,
          value: documents.filter((doc) => doc.publicity === 'public').length,
          label: 'Public Documents',
          color: 'success',
        },
        ...(canViewPrivate
          ? [
              {
                icon: PrivateIcon,
                value: documents.filter((doc) => doc.publicity === 'private').length,
                label: 'Private Documents',
                color: 'error',
              },
            ]
          : []),
        {
          icon: EventIcon,
          value: documents.filter((doc) => doc.event).length,
          label: 'With Events',
          color: 'primary',
        },
      ]
    : [
        {
          icon: FolderIcon,
          value: documents.length,
          label: 'Total Documents',
          color: 'info',
        },
        {
          icon: EventIcon,
          value: documents.filter((doc) => doc.event).length,
          label: 'With Events',
          color: 'primary',
        },
        {
          icon: EventIcon,
          value: documents.filter((doc) => !doc.event).length,
          label: 'Without Events',
          color: 'warning',
        },
      ];

  const columns = [
    {
      key: 'document_name',
      label: 'Document',
      icon: DocumentIcon,
      minWidth: 220,
      render: (document) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            sx={{
              width: 44,
              height: 44,
              bgcolor: theme.palette.success.main,
              boxShadow: 2,
              border: `2px solid ${alpha(theme.palette.success.main, 0.1)}`,
            }}
          >
            <DocumentIcon />
          </Avatar>
          <Box>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: '#E2E8F0',
              }}
            >
              {document.document_name}
            </Typography>
            <Typography variant="caption" sx={{ color: '#94A3B8' }}>
              PDF Document
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      key: 'event.name',
      label: 'Event',
      icon: EventIcon,
      minWidth: 150,
      render: (document) =>
        document.event ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EventIcon fontSize="small" sx={{ color: '#E2E8F0' }} />
            <Typography variant="body2" sx={{ color: '#E2E8F0' }}>{document.event.name}</Typography>
          </Box>
        ) : (
          <Typography variant="body2" sx={{ color: '#94A3B8' }}>
            No event
          </Typography>
        ),
    },
    {
      key: 'user.name',
      label: 'Uploaded By',
      icon: PersonIcon,
      minWidth: 160,
      render: (document) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            sx={{
              width: 32,
              height: 32,
              bgcolor: theme.palette.primary.main,
              fontSize: '0.875rem',
            }}
          >
            {document.user?.name?.charAt(0)?.toUpperCase() || '?'}
          </Avatar>
          <Typography variant="body2" sx={{ color: '#E2E8F0' }}>{document.user?.name || '-'}</Typography>
        </Box>
      ),
    },
    {
      key: 'publicity',
      label: 'Publicity',
      icon: SecurityIcon,
      align: 'center',
      minWidth: 140,
      render: (document) => (
        <Chip
          icon={getPublicityIcon(document.publicity)}
          label={(document.publicity || '').toUpperCase()}
          color={getPublicityColor(document.publicity)}
          size="small"
          sx={{ fontWeight: 500, boxShadow: 1 }}
        />
      ),
    },
    {
      key: 'created_at',
      label: 'Created',
      icon: EventIcon,
      minWidth: 140,
      render: (document) => (
        <Typography variant="body2" sx={{ fontWeight: 500, color: '#E2E8F0' }}>
          {format(new Date(document.created_at), 'MMM dd, yyyy')}
        </Typography>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      align: 'right',
      sortable: false,
      minWidth: 140,
      render: (document) => (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Tooltip title="Download Document" arrow>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleDownload(document);
              }}
              sx={{
                color: theme.palette.success.main,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.success.main, 0.1),
                },
              }}
            >
              <CloudDownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="View Document" arrow>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/documents/${document.id}`);
              }}
              sx={{
                color: theme.palette.info.main,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.info.main, 0.1),
                },
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {canManage && (
            <>
              <Tooltip title="Edit Document" arrow>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/documents/edit/${document.id}`);
                  }}
                  sx={{
                    color: theme.palette.warning.main,
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.warning.main, 0.1),
                    },
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete Document" arrow>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(document);
                  }}
                  sx={{
                    color: theme.palette.error.main,
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.error.main, 0.1),
                    },
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      ),
    },
  ];

  if (!user) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <FeedbackMessage
          message="Please log in to view this page."
          severity="error"
          sx={{ fontFamily: "'DM Sans',sans-serif", fontSize: '0.9rem', background: alpha('#450a0a',0.92), border: '1px solid rgba(248,113,113,0.3)', color: '#fca5a5', boxShadow: '0 4px 20px rgba(239,68,68,0.12)' }}
        />
      </Container>
    );
  }

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          gap: 2,
        }}
      >
        <Loading message="Loading documents list" size={56} variant="primary" />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header Section */}
      <Header
        title="Document"
        subtitle="Manage and organize project documents"
        icon={DocumentIcon}
        actionButton={
          canManage ? (
            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              onClick={() => navigate('/documents/add')}
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.22)',
                backdropFilter: 'blur(10px)',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.34)',
                },
                borderRadius: 2,
                px: 2.5,
                py: 1.5,
              }}
            >
              Add Document
            </Button>
          ) : null
        }
      />

      {/* Error Message */}
      <FeedbackMessage
        message={error}
        severity="error"
        onClose={() => setError(null)}
        sx={{ mb: 3, fontFamily: "'DM Sans',sans-serif", fontSize: '0.9rem', background: alpha('#450a0a',0.92), border: '1px solid rgba(248,113,113,0.3)', color: '#fca5a5', boxShadow: '0 4px 20px rgba(239,68,68,0.12)' }}
      />

      {/* Success Message */}
      <FeedbackMessage
        message={success}
        severity="success"
        onClose={() => setSuccess(null)}
        sx={{ mb: 3, fontFamily: "'DM Sans',sans-serif", fontSize: '0.9rem', fontWeight: 500, background: alpha(SUCCESS, 0.12), border: `1px solid ${alpha(SUCCESS, 0.25)}`, color: alpha(SUCCESS, 0.95) }}
      />

      {/* Stats Cards */}
      <StatCards
        stats={statsData}
        onCardClick={handleStatCardClick}
        activeIndex={
          canManage
            ? activeStatFilter === 'public'
              ? 1
              : activeStatFilter === 'private'
              ? 2
              : activeStatFilter === 'event'
              ? (canViewPrivate ? 3 : 2)
              : 0
            : activeStatFilter === 'event'
            ? 1
            : activeStatFilter === 'without_event'
            ? 2
            : 0
        }
      />

      <ListFilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        showFilters={showFilters}
        onToggleFilters={() => setShowFilters(!showFilters)}
        filters={filters}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
        filterOptions={filterOptions}
        searchPlaceholder="Search by document name..."
      />

      {/* Documents Table */}
      <ListBulkToolbar
        config={DOCUMENT_BULK_CONFIG}
        currentUser={user}
        selectedIds={selectedIds}
        onClearSelection={clearSelection}
        {...createBulkHandlers({
          endpoint: DOCUMENT_BULK_CONFIG.endpoint,
          setItems: setDocuments,
          selectedIds,
          clearSelection,
          setSuccess,
          successLabel: 'document(s)',
          refetch: fetchDocuments,
          setError
        })}
      />


      <TableList
        columns={columns}
        data={filteredDocuments}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        {...bulkTableProps(DOCUMENT_BULK_CONFIG, user, selectedIds, setSelectedIds)}
        loading={loading}
        emptyState={{
          icon: <DocumentIcon />,
          title: 'No documents found',
          description:
            searchTerm || Object.keys(filters).length > 0
              ? 'Try adjusting your filters to see more documents.'
              : 'Get started by uploading your first document.',
        }}
        onRowClick={(document) => navigate(`/documents/${document.id}`)}
      />

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedDocument(null);
        }}
        onConfirm={confirmDelete}
        itemType="document"
        itemName={selectedDocument?.document_name}
        description="This action cannot be undone and will permanently remove the document."
        confirmLabel="Delete Document"
      />
    </Container>
  );
};

export default DocumentList;
